import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ChevronLeft, ChevronRight, Pause, Play, Star, Quote } from 'lucide-react';
import { REVIEW_HIGHLIGHTS } from './reviewHighlights';

const AUTOPLAY_MS = 7000;

// Width of one card plus the gap between cards.
const getStep = (track) => {
  const first = track && track.children[0];
  if (!first) return 0;
  const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
  return first.getBoundingClientRect().width + gap;
};

export const ReviewHighlightsSection = ({
  title = 'Recent Customer Reviews',
  subtitle = 'Real feedback from customers who recently used Swift Move and Clean.',
  maxItems = REVIEW_HIGHLIGHTS.length,
  className = 'py-16 bg-white',
}) => {
  const reviews = REVIEW_HIGHLIGHTS.slice(0, maxItems);
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [positions, setPositions] = useState(reviews.length);
  const [hovering, setHovering] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(Boolean(query && query.matches));
  }, []);

  // Number of stops depends on how many cards fit on screen at once.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const update = () => {
      const step = getStep(track);
      if (!step) return;
      setPositions(Math.max(1, Math.round((track.scrollWidth - track.clientWidth) / step) + 1));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [reviews.length]);

  const goTo = useCallback(
    (index) => {
      const track = trackRef.current;
      if (!track) return;
      track.scrollTo({ left: index * getStep(track), behavior: reducedMotion ? 'auto' : 'smooth' });
    },
    [reducedMotion]
  );

  const handleScroll = () => {
    const track = trackRef.current;
    const step = getStep(track);
    if (!step) return;
    setActive(Math.min(positions - 1, Math.max(0, Math.round(track.scrollLeft / step))));
  };

  const autoplay = !hovering && !userPaused && !reducedMotion && positions > 1;

  useEffect(() => {
    if (!autoplay) return undefined;
    const timer = setInterval(() => {
      if (!document.hidden) goTo((active + 1) % positions);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [autoplay, active, positions, goTo]);

  return (
    <section
      className={className}
      aria-roledescription="carousel"
      aria-label={title}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onFocus={() => setHovering(true)}
      onBlur={() => setHovering(false)}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-orange-100 text-bright-orange hover:bg-orange-100">
              Verified Highlights
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-4">
              {title}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {subtitle}
            </p>
          </div>

          <div
            ref={trackRef}
            onScroll={handleScroll}
            aria-live="off"
            className="hide-scrollbar flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4"
          >
            {reviews.map((review, index) => (
              <div
                key={review.id}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${reviews.length}`}
                className="shrink-0 snap-start basis-full md:basis-[calc((100%-1.5rem)/2)] xl:basis-[calc((100%-3rem)/3)]"
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{review.name}</h3>
                        <p className="text-sm text-gray-500">{review.source}</p>
                      </div>
                      <span className="text-sm font-semibold text-sky-blue">{review.dateLabel}</span>
                    </div>

                    <div className="flex items-center gap-1 mb-4" role="img" aria-label={`${review.rating} out of 5 stars`}>
                      {[...Array(review.rating)].map((_, starIndex) => (
                        <Star key={starIndex} className="h-4 w-4 text-yellow-400 fill-current" />
                      ))}
                    </div>

                    <div className="relative">
                      <Quote className="h-8 w-8 text-sky-blue/15 absolute -top-1 -left-1" />
                      <p className="text-gray-700 leading-relaxed pl-6">
                        {review.text}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {positions > 1 && (
            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                type="button"
                aria-label="Previous reviews"
                onClick={() => goTo((active - 1 + positions) % positions)}
                className="rounded-full border border-gray-300 bg-white p-2 text-gray-700 shadow-sm transition-colors hover:bg-gray-100"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-2" role="group" aria-label="Choose reviews">
                {Array.from({ length: positions }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    aria-label={`Show reviews ${index + 1} of ${positions}`}
                    aria-current={index === active}
                    onClick={() => goTo(index)}
                    className={`h-2.5 w-2.5 rounded-full transition-colors ${
                      index === active ? 'bg-sky-600' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>

              <button
                type="button"
                aria-label="Next reviews"
                onClick={() => goTo((active + 1) % positions)}
                className="rounded-full border border-gray-300 bg-white p-2 text-gray-700 shadow-sm transition-colors hover:bg-gray-100"
              >
                <ChevronRight className="h-5 w-5" />
              </button>

              {!reducedMotion && (
                <button
                  type="button"
                  aria-label={userPaused ? 'Resume automatic scrolling' : 'Pause automatic scrolling'}
                  onClick={() => setUserPaused((current) => !current)}
                  className="rounded-full border border-gray-300 bg-white p-2 text-gray-700 shadow-sm transition-colors hover:bg-gray-100"
                >
                  {userPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
